// ═══════════════════════════════════════════════════════════════
// 📅 RESCISSION CALENDAR LANDING PAGE — Mockup Edition
// Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network
// Rebuilt 1:1 from the approved design mockup: warm cream canvas,
// navy preview panel, compact white form, gold-bordered success
// screen with the purple gifts box. All machinery preserved:
// lead capture → marketing_leads, backup email, UTM tracking,
// silent visitor counter with threshold-guarded social proof.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const PDF_URL = 'https://utrvslwjxcvnctjfomit.supabase.co/storage/v1/object/public/resources/LCA_Rescission_Calendar_2026.pdf'

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; }
  .lp-wrap { min-height:100vh; background:#F7F6F1; font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; color:#1A1A1A; padding:26px 14px 40px; display:flex; align-items:flex-start; justify-content:center; }
  .lp-container { width:100%; max-width:660px; background:#FDF6E3; border:0.5px solid #E3DCC9; border-radius:14px; padding:8px 16px 18px; }


  /* HERO — compact, centered, serif display */
  .lp-hero { text-align:center; padding:20px 20px 16px; }
  .lp-seal-circle { width:56px; height:56px; border-radius:50%; background:#0D1B3E; margin:0 auto 10px; display:flex; align-items:center; justify-content:center; }
  .lp-seal { width:38px; height:38px; object-fit:contain; }
  .lp-title { font-family:Georgia,serif; font-size:24px; font-weight:500; color:#0D1B3E; margin:0; line-height:1.3; }
  .lp-subtitle { font-size:13px; color:#9A7A30; margin-top:5px; font-weight:500; }
  .lp-from-tribe { font-size:12px; color:#6B2FA0; margin-top:2px; }
  @media (max-width: 600px) { .lp-title { font-size:21px; } }

  /* TWO COLUMN GRID */
  .lp-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:13px; padding:0 4px 16px; align-items:start; }
  @media (max-width: 620px) { .lp-grid { grid-template-columns:1fr; } }

  /* LEFT: NAVY PREVIEW PANEL */
  .lp-preview { background:#0D1B3E; border-radius:12px; padding:16px; }
  .lp-preview-pages { display:inline-block; background:#C9A84C; color:#0D1B3E; font-size:10px; font-weight:500; padding:3px 10px; border-radius:10px; }
  .lp-preview-pdf-name { font-family:Georgia,serif; font-size:19px; color:#F8F7F4; margin-top:10px; }
  .lp-preview-pdf-year { font-size:15px; color:#C9A84C; }
  .lp-preview-divider { border-top:0.5px solid #2A3B5E; margin:12px 0 10px; }
  .lp-preview-title { font-size:11px; color:#C9A84C; font-weight:500; margin-bottom:6px; letter-spacing:0.05em; }
  .lp-feature-list { list-style:none; padding:0; margin:0; }
  .lp-feature-item { display:flex; gap:7px; font-size:11px; color:#D8DCE6; line-height:1.8; }
  .lp-feature-icon { color:#8BB6A2; flex-shrink:0; }

  /* RIGHT: FORM CARD */
  .lp-form-card { background:#fff; border-radius:12px; padding:16px; border:0.5px solid #E8E2D4; }
  .lp-form-title { font-size:15px; font-weight:500; color:#0D1B3E; margin:0; }
  .lp-form-sub { font-size:11px; color:#7A7870; margin:1px 0 12px; }
  .lp-field { margin-bottom:9px; }
  .lp-label { display:block; font-size:10px; color:#9A7A30; font-weight:500; margin-bottom:3px; letter-spacing:0.03em; }
  .lp-input, .lp-select { width:100%; padding:7px 10px; border:1px solid #E0DCCF; border-radius:8px; font-size:12px; font-family:inherit; background:#fff; }
  .lp-input:focus, .lp-select:focus { outline:none; border-color:#C9A84C; }
  .lp-btn { width:100%; padding:11px; border:none; border-radius:10px; background:#0D1B3E; color:#C9A84C; font-size:13px; font-weight:500; cursor:pointer; font-family:inherit; margin-top:2px; }
  .lp-btn:hover:not(:disabled) { background:#1A3A5E; }
  .lp-btn:disabled { opacity:0.6; cursor:not-allowed; }
  .lp-privacy { font-size:10px; color:#B8B5AB; text-align:center; margin-top:10px; line-height:1.5; }
  .lp-privacy .lp-heart { color:#6B2FA0; }
  .lp-social-proof { font-size:11px; color:#9A7A30; font-style:italic; text-align:center; margin-top:10px; }
  .lp-error { background:#FDF2F2; border:1px solid #E24B4A; color:#A32D2D; padding:9px 12px; border-radius:8px; font-size:12px; margin-bottom:10px; }

  /* SUCCESS SCREEN — gold-bordered card */
  .lp-success { margin:0 4px 16px; background:#fff; border:1.5px solid #C9A84C; border-radius:12px; padding:18px 16px; text-align:center; }
  .lp-success-icon { font-size:26px; }
  .lp-success-title { font-family:Georgia,serif; font-size:18px; color:#0D1B3E; margin-top:4px; }
  .lp-success-text { font-size:12px; color:#5F5E5A; margin:6px 0 12px; line-height:1.6; }
  .lp-download-btn { display:inline-block; background:#0D1B3E; color:#C9A84C; border-radius:10px; padding:10px 22px; font-size:13px; font-weight:500; text-decoration:none; }
  .lp-download-btn:hover { background:#1A3A5E; }
  .lp-email-note { font-size:10px; color:#888780; font-style:italic; margin-top:8px; line-height:1.6; }

  /* PURPLE GIFTS BOX */
  .lp-tribe-invite { background:#F7F1FA; border:0.5px solid #D9C4EA; border-radius:10px; padding:12px; margin-top:14px; text-align:left; }
  .lp-tribe-title { font-size:13px; font-weight:500; color:#6B2FA0; }
  .lp-tribe-text { font-size:11px; color:#4A4840; line-height:1.7; margin-top:4px; }
  .lp-tribe-btn { display:inline-block; background:#6B2FA0; color:#fff; border-radius:8px; padding:7px 16px; font-size:11px; font-weight:500; margin-top:10px; text-decoration:none; }
  .lp-tribe-btn:hover { background:#552486; }

  .lp-tribe-standalone { margin:0 4px 16px; }

  /* FOOTER */
  .lp-footer { text-align:center; padding:14px 16px 4px; color:#8A877D; font-size:11px; line-height:1.8; }
  .lp-footer strong { color:#0D1B3E; }
  .lp-footer a { color:#9A7A30; text-decoration:none; }
`

export default function RescissionCalendarLanding() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', notary_state: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [utmParams, setUtmParams] = useState({})
  const [stats, setStats] = useState(null)   // 👁️ visit + download counts

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setUtmParams({
      utm_source: params.get('utm_source'),
      utm_campaign: params.get('utm_campaign'),
    })
  }, [])

  // 👁️ VISITOR COUNTER: silently logs the visit (with referrer + UTM for
  // Nancy's analytics) and returns totals. Social proof shows only once
  // downloads pass a healthy threshold.
  useEffect(() => {
    let cancelled = false
    supabase.rpc('track_landing_visit', {
      p_page: 'rescission_calendar_2026',
      p_referrer: document.referrer || null,
      p_utm: new URLSearchParams(window.location.search).get('utm_source'),
    }).then(({ data, error: rpcErr }) => {
      if (!cancelled && !rpcErr && data) setStats(data)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  async function submit(e) {
    if (e) e.preventDefault()
    setError('')

    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('Please fill in all required fields!!')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address!!')
      return
    }

    setSubmitting(true)
    try {
      const { error: dbErr } = await supabase.from('marketing_leads').insert({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        notary_state: form.notary_state || null,
        lead_source: 'rescission_calendar_2026',
        utm_source: utmParams.utm_source,
        utm_campaign: utmParams.utm_campaign,
        user_agent: navigator.userAgent.slice(0, 200),
      })
      if (dbErr) throw dbErr

      try {
        await fetch('/.netlify/functions/send-lead-magnet', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email: form.email.trim().toLowerCase(),
            notary_state: form.notary_state || null,
            pdf_url: PDF_URL,
            lead_source: 'rescission_calendar_2026',
          }),
        })
      } catch (emailErr) {
        console.warn('Email send failed (lead is saved):', emailErr)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Lead submit error:', err)
      setError(`Submission failed: ${err.message}. Please try again or email nancy@loanclosingagent.com directly!!`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="lp-wrap">
        <div className="lp-container">

          {/* HERO */}
          <div className="lp-hero">
            <div className="lp-seal-circle">
              <img src="/lca_logo_quill_map.png" alt="LCA Logo" className="lp-seal" />
            </div>
            <h1 className="lp-title">Get your FREE 2026 rescission calendar</h1>
            <div className="lp-subtitle">From the LCA to YOU!!</div>
            <div className="lp-from-tribe">Built by a Notary, for Notaries!!</div>
          </div>

          {submitted ? (
            // ─── SUCCESS STATE ───
            <div className="lp-success">
              <div className="lp-success-icon">🎉</div>
              <div className="lp-success-title">You're all set, {form.first_name}!!</div>
              <div className="lp-success-text">
                Your calendar is ready right now — and a backup copy is on its way to your inbox!!
              </div>

              <a href={PDF_URL} target="_blank" rel="noreferrer" className="lp-download-btn">
                ⬇ Download your calendar
              </a>

              <div className="lp-email-note">
                Check your email at <strong>{form.email}</strong> for a backup copy<br />
                (Don't see it? Check your spam folder!!)
              </div>

              <div className="lp-tribe-invite">
                <div className="lp-tribe-title">💜 Want MORE notary magic??</div>
                <div className="lp-tribe-text">
                  Joining is FREE — and two more gifts wait inside: 🎓 the full "Mastering the Rescission Calendar" course (with printable Student Guide) and 🎙️ The LCA Podcast — learn on the road between signings!! Plus a private community where notaries grow together, share wins, and shape the future of our industry!!
                </div>
                <a href="/register" className="lp-tribe-btn">Join the Tribe — FREE 💜</a>
              </div>
            </div>
          ) : (
            // ─── FORM STATE ───
            <div className="lp-grid">

              {/* LEFT: Navy preview panel */}
              <div className="lp-preview">
                <div className="lp-preview-pages">14 PAGES · PRINT-READY</div>
                <div className="lp-preview-pdf-name">Rescission Calendar</div>
                <div className="lp-preview-pdf-year">2026</div>
                <div className="lp-preview-divider"></div>
                <div className="lp-preview-title">WHAT'S INSIDE:</div>
                <ul className="lp-feature-list">
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>TILA rules page + all 11 federal holidays</span></li>
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>12 monthly calendars for all of 2026</span></li>
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>"Ends MM/DD" on every business day</span></li>
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>Holidays marked and excluded automatically</span></li>
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>Print-friendly for your notary bag</span></li>
                  <li className="lp-feature-item"><span className="lp-feature-icon">✓</span><span>100% FREE — no strings attached</span></li>
                </ul>
              </div>

              {/* RIGHT: Form card */}
              <div className="lp-form-card">
                <div className="lp-form-title">Get your FREE copy</div>
                <div className="lp-form-sub">Tell us where to send it!!</div>

                {error && <div className="lp-error">⚠️ {error}</div>}

                <form onSubmit={submit}>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="lp-first">FIRST NAME *</label>
                    <input id="lp-first" className="lp-input" type="text" required value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Jane" />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="lp-last">LAST NAME *</label>
                    <input id="lp-last" className="lp-input" type="text" required value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Doe" />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="lp-email">EMAIL *</label>
                    <input id="lp-email" className="lp-input" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@email.com" />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="lp-state">STATE (OPTIONAL)</label>
                    <input id="lp-state" className="lp-input" type="text" value={form.notary_state} onChange={e => setForm(f => ({ ...f, notary_state: e.target.value }))} placeholder="Florida" />
                  </div>
                  <button type="submit" className="lp-btn" disabled={submitting}>
                    {submitting ? 'Sending...' : '⬇ Send me my free calendar'}
                  </button>
                </form>

                {stats && stats.downloads >= 25 && (
                  <div className="lp-social-proof">🔥 {stats.downloads} notaries have already grabbed theirs!!</div>
                )}
              </div>
            </div>
          )}

          {/* 💜 THE GIFTS CARD — always visible at the bottom of the page */}
          {!submitted && (
            <div className="lp-tribe-invite lp-tribe-standalone">
              <div className="lp-tribe-title">💜 Want MORE notary magic??</div>
              <div className="lp-tribe-text">
                Joining is FREE — and two more gifts wait inside: 🎓 the full "Mastering the Rescission Calendar" course (with printable Student Guide) and 🎙️ The LCA Podcast — learn on the road between signings!! Plus a private community where notaries grow together.
              </div>
              <a href="/register" className="lp-tribe-btn">Join the Tribe — FREE 💜</a>
            </div>
          )}

          {/* FOOTER */}
          <div className="lp-footer">
            <strong>Loan Closing Agent, LLC.</strong> · DBA Nationwide Notary Professionals Network<br />
            <a href="https://loanclosingagent.com">loanclosingagent.com</a> &nbsp;|&nbsp; (904) 865-0133 &nbsp;|&nbsp; <a href="/login">Notary Login →</a><br />
            Veteran &amp; Hispanic Family Owned Small Business
          </div>

        </div>
      </div>
    </>
  )
}
